import {createSlice} from '@reduxjs/toolkit';

const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    loading: false,
    dataLoaded: false,
    taskConfigsLoaded: false,
    implementsLoaded: false,
    getSettingsTrigger: false,
    getVehicleSettingsTrigger: false,
    implementsBulkEditMode: false,
    tasksBulkEditMode: false,
    inspectionItemsBulkEditMode: false,
    activeDevices: {
      'samsara': {},
      'geotab': {},
      'johndeere': {},
      'pellenc': {},
    },
    implementSNDict: {},
    implementsFiltered: {},
    implementLastLocation: {},
    implementLabelsDict: {},
    implementsBulkEditErrors: {},
    assetLabelsBulkEditErrors: {},
    inspectionItemsBulkEditErrors: {},
    tasksBulkEditErrors: {},
    taskConfigDict: {},
    devicesList: [],
    databaseUsers: [],
    operators: [],
    settingsGroups: {},
    vehicleDict: {},
    vehicleLabelsDict: {},
    vehicles: [],
    vehiclesIcActive: [],
    vehiclesBulkEditErrors: {},
    vehiclesUnfiltered: [],
    zonesDicts: {
      blocks: {},
      fields: {},
      regions: {},
      nonAcAreas: {},
    },
    labels: [],
    assetLabelsDict: {},
    inspectionItemsDict: {},
    btTags: [],
    btHubs: [],
    keyFobs: [],
    inspectionItems: [],
    tableLoading: {
      'task': false,
      'implements': false,
      'assetLabels': false,
      'inspectionItems': false,
    },
    bulkEditAssetLabels: {},
    bulkEditVehicles: {},
    bulkEditImplements: {},
    bulkEditTasks: {},
    bulkEditInspectionItems: {},
    reportConfigsDict: {},
    scorecards: [],
    gobalVehicleDatabase: [],
    refreshing: {
      users: false,
      assets: false,
      tasks: false,
      reports: false,
    },
    usersTabVisibleColumns: {
      username: true,
      email: true,
      name: true,
      group: true,
      jobTitle: true,
      jobRole: true,
      role: true,
      language: true,
      status: true,
      phoneNumber: true,
      nfcKey: true,
      notes: false,
    },
  },
  reducers: {
    updateLoading(state, action) {
      state.loading = action.payload;
    },
    setDataLoaded(state, action) {
      state.dataLoaded = action.payload;
    },
    setTaskConfigsLoaded(state, action) {
      state.taskConfigsLoaded = action.payload;
    },
    setImplementsLoaded(state, action) {
      state.implementsLoaded = action.payload;
    },
    updateScorecards(state, action) {
      state.scorecards = action.payload;
    },
    updateTableLoading(state, action) {
      state.tableLoading = {
        ...state.tableLoading,
        [action.payload.table]: action.payload.status,
      };
    },
    updateRefreshing(state, action) {
      state.refreshing = {
        ...state.refreshing,
        [action.payload.key]: action.payload.status,
      };
    },
    setImplementsBulkEditMode(state, action) {
      state.implementsBulkEditMode = action.payload;
    },
    setTasksBulkEditMode(state, action) {
      state.tasksBulkEditMode = action.payload;
    },
    setAssetLabelsBulkEditMode(state, action) {
      state.assetLabelsBulkEditMode = action.payload;
    },
    setInspectionItemsBulkEditMode(state, action) {
      state.inspectionItemsBulkEditMode = action.payload;
    },
    updateDevices(state, action) {
      if (action.payload != null) {
        // Multi-org shape (connectionid-scoped): payload[provider][connectionid][deviceId] = device.
        // Iterate down to the device level so the `active` filter drops inactive DEVICES (not whole
        // connections). devicesList is the flat list of all devices across connections.
        const devicesActive = JSON.parse(JSON.stringify(action.payload));
        const devicesList = [];
        Object.keys(action.payload).forEach(function (source) {
          Object.keys(action.payload[source] || {}).forEach(function (connectionid) {
            const connMap = action.payload[source][connectionid] || {};
            Object.keys(connMap).forEach(function (deviceKey) {
              const device = connMap[deviceKey];
              devicesList.push(device);
              if (!device || !device.active) {
                delete devicesActive[source][connectionid][deviceKey];
              }
            });
          });
        });

        state.devicesList = devicesList;
        state.activeDevices = devicesActive;
      }
    },
    updateDatabaseUsers(state, action) {
      state.databaseUsers = action.payload;
    },
    updateOperators(state, action) {
      state.operators = action.payload;
    },
    updateSettingsGroups(state, action) {
      state.settingsGroups = action.payload;
    },
    updateVehicleLabels(state, action) {
      const vehicleLabels = action.payload;

      const vehicleLabelsDict = {};
      vehicleLabels.forEach((label) => {
        vehicleLabelsDict[label.id] = label;
      });
      state.vehicleLabelsDict = vehicleLabelsDict;
    },
    updateVehiclesData(state, action) {
      state.vehicles = action.payload;
    },
    setVehiclesBulkEditErrors(state, action) {
      state.vehiclesBulkEditErrors = action.payload;
    },
    setImplementsBulkEditErrors(state, action) {
      state.implementsBulkEditErrors = action.payload;
    },
    setTasksBulkEditErrors(state, action) {
      state.tasksBulkEditErrors = action.payload;
    },
    setInspectionItemsBulkEditErrors(state, action) {
      state.inspectionItemsBulkEditErrors = action.payload;
    },
    setAssetLabelsBulkEditErrors(state, action) {
      state.assetLabelsBulkEditErrors = action.payload;
    },
    updateVehiclesIcActiveData(state, action) {
      state.vehiclesIcActive = action.payload;
    },
    updateVehiclesUnfilteredData(state, action) {
      const vehiclesUnfiltered = action.payload;
      state.vehiclesUnfiltered = vehiclesUnfiltered;

      const vehicleDict = {};
      vehiclesUnfiltered.forEach((vehicle) => {
        vehicleDict[vehicle.serialNumber] = vehicle;
      });
      state.vehicleDict = vehicleDict;
    },
    updateTaskConfigDict(state, action) {
      state.taskConfigDict = action.payload;
    },
    updateImplementSNDict(state, action) {
      state.implementSNDict = action.payload;
    },
    updateImplementsFiltered(state, action) {
      state.implementsFiltered = action.payload;
    },
    updateImplementLastLocation(state, action) {
      state.implementLastLocation = action.payload;
    },
    updateImplementLabelsDict(state, action) {
      const implementLabels = action.payload;
      const implementLabelsDict = {};
      implementLabels.forEach((label) => {
        implementLabelsDict[label.id] = label;
      });
      state.implementLabelsDict = implementLabelsDict;
    },
    updateReportConfigsDict(state, action) {
      state.reportConfigsDict = action.payload;
    },
    updateZonesData(state, action) {
      const allZoneData = action.payload;

      state.zonesDicts = {
        blocks: allZoneData.blocks,
        fields: allZoneData.fields,
        regions: allZoneData.regions,
        nonAcAreas: allZoneData.nonAcAreas,
      };
    },
    updateLabelsData(state, action) {
      const labels = action.payload;
      labels.sort((a, b) => {
        return a.database_label.localeCompare(b.database_label);
      });
      state.labels = labels;
    },
    updateBtTagsData(state, action) {
      state.btTags = action.payload;
    },
    updateBtHubsData(state, action) {
      state.btHubs = action.payload;
    },
    updateKeyFobsData(state, action) {
      state.keyFobs = Array.isArray(action.payload) ? action.payload : [];
    },
    updateInspectionItemsData(state, action) {
      const inspectionItems = [];
      action.payload.forEach((item) => {
        if (!item.hasOwnProperty('service_on_failure')) {
          item.service_on_failure = false;
        }
        inspectionItems.push(item);
      });
      state.inspectionItems = inspectionItems;
    },
    triggerGetSettingsData(state) {
      state.getSettingsTrigger = !state.getSettingsTrigger;
      state.dataLoaded = false;
      state.taskConfigsLoaded = false;
      state.implementsLoaded = false;
    },
    triggerGetVehicleSettingsData(state) {
      state.getVehicleSettingsTrigger = !state.getVehicleSettingsTrigger;
    },
    updateBulkEditVehicles(state, action) {
      const tempData = {
        ...(Object.hasOwnProperty.call(state.bulkEditVehicles, action.payload.vehicleSN)
          ? state.bulkEditVehicles[action.payload.vehicleSN]
          : {}),
        ...action.payload.data,
      };
      state.bulkEditVehicles = {
        ...state.bulkEditVehicles,
        [action.payload.vehicleSN]: tempData,
      };
    },
    updateBulkEditImplements(state, action) {
      const tempData = {
        ...(Object.hasOwnProperty.call(state.bulkEditImplements, action.payload.serialNumber)
          ? state.bulkEditImplements[action.payload.serialNumber]
          : {}),
        ...action.payload.data,
      };
      state.bulkEditImplements = {
        ...state.bulkEditImplements,
        [action.payload.serialNumber]: tempData,
      };
    },
    updateBulkEditInspectionItems(state, action) {
      let tempData;
      // Append newly added vehicle type
      if (
        action.payload.hasOwnProperty('data') &&
        action.payload.data.hasOwnProperty('vehicleType') &&
        !Array.isArray(action.payload.data.vehicleType)
      ) {
        tempData = {
          ...(Object.hasOwnProperty.call(state.bulkEditInspectionItems, action.payload.id)
            ? state.bulkEditInspectionItems[action.payload.id]
            : {}),
        };
        if (!tempData.vehicleType.includes(action.payload.data.vehicleType)) {
          tempData.vehicleType.push(action.payload.data.vehicleType);
        }
      }
      //  Delete newly deleted vehicle type
      else if (
        action.payload.hasOwnProperty('data') &&
        action.payload.data.hasOwnProperty('vehicleType') &&
        action.payload.data.hasOwnProperty('deletedVal')
      ) {
        tempData = {
          ...(Object.hasOwnProperty.call(state.bulkEditInspectionItems, action.payload.id)
            ? state.bulkEditInspectionItems[action.payload.id]
            : {}),
        };
        const index = tempData.vehicleType.indexOf(action.payload.data.deletedVal);
        if (index > -1) {
          tempData.vehicleType.splice(index, 1);
        }
      }
      // Append newly added implement labels
      else if (
        action.payload.hasOwnProperty('data') &&
        action.payload.data.hasOwnProperty('implementLabels') &&
        !Array.isArray(action.payload.data.implementLabels)
      ) {
        tempData = {
          ...(Object.hasOwnProperty.call(state.bulkEditInspectionItems, action.payload.id)
            ? state.bulkEditInspectionItems[action.payload.id]
            : {}),
        };
        if (!tempData.implementLabels.includes(action.payload.data.implementLabels)) {
          tempData.implementLabels.push(action.payload.data.implementLabels);
        }
      }
      //  Delete newly deleted implement labels
      else if (
        action.payload.hasOwnProperty('data') &&
        action.payload.data.hasOwnProperty('implementLabels') &&
        action.payload.data.hasOwnProperty('deletedVal')
      ) {
        tempData = {
          ...(Object.hasOwnProperty.call(state.bulkEditInspectionItems, action.payload.id)
            ? state.bulkEditInspectionItems[action.payload.id]
            : {}),
        };
        const index = tempData.implementLabels.indexOf(action.payload.data.deletedVal);
        if (index > -1) {
          tempData.implementLabels.splice(index, 1);
        }
      }
      //  Normal update
      else {
        tempData = {
          ...(Object.hasOwnProperty.call(state.bulkEditInspectionItems, action.payload.id)
            ? state.bulkEditInspectionItems[action.payload.id]
            : {}),
          ...action.payload.data,
        };
      }

      state.bulkEditInspectionItems = {
        ...state.bulkEditInspectionItems,
        [action.payload.id]: tempData,
      };
    },
    updateBulkEditTasks(state, action) {
      const tempData = {
        ...(Object.hasOwnProperty.call(state.bulkEditTasks, action.payload.taskId)
          ? state.bulkEditTasks[action.payload.taskId]
          : {}),
        ...action.payload.data,
      };
      state.bulkEditTasks = {
        ...state.bulkEditTasks,
        [action.payload.taskId]: tempData,
      };
    },
    updateBulkEditAssetLabels(state, action) {
      const tempData = {
        ...(Object.hasOwnProperty.call(state.bulkEditAssetLabels, action.payload.id)
          ? state.bulkEditAssetLabels[action.payload.id]
          : {}),
        ...action.payload.data,
      };
      state.bulkEditAssetLabels = {
        ...state.bulkEditAssetLabels,
        [action.payload.id]: tempData,
      };
    },
    initializeBulkEditImplements(state, action) {
      state.bulkEditImplements = action.payload;
    },
    initializeBulkEditTasks(state, action) {
      state.bulkEditTasks = action.payload;
    },
    initializeBulkEditVehicles(state, action) {
      state.bulkEditVehicles = action.payload;
    },
    initializeBulkEditAssetLabels(state, action) {
      state.assetLabelsDict = action.payload;
      state.bulkEditAssetLabels = action.payload;
    },
    initializeBulkEditInspectionItems(state, action) {
      state.inspectionItemsDict = action.payload;
      state.bulkEditInspectionItems = action.payload;
    },
    updateGobalVehicleDatabase(state, action) {
      state.gobalVehicleDatabase = action.payload;
    },
    updateUsersTabVisibleColumns(state, action) {
      state.usersTabVisibleColumns = action.payload;
    },
  },
});

function getSettingsData() {
  return async (dispatch, getState) => {
    const hasExistingData = getState().settings.vehicles?.length > 0;

    if (hasExistingData) {
      dispatch(settingsSlice.actions.updateRefreshing({key: 'users', status: true}));
      dispatch(settingsSlice.actions.updateRefreshing({key: 'assets', status: true}));
      dispatch(settingsSlice.actions.updateRefreshing({key: 'reports', status: true}));
    } else {
      dispatch(settingsSlice.actions.updateLoading(true));
    }

    const getDevicesRequest = fetch('/getDevices', {cache: 'no-store'});
    const getDatabaseUsersRequest = fetch('/settings/getDatabaseUsers', {cache: 'no-store'});
    const getOperatorsRequest = fetch('/settings/getOperators?includeArchived=true', {cache: 'no-store'});
    const getSettingsGroupsRequest = fetch('/settings/getSettingsGroups', {cache: 'no-store'});
    const getVehiclesRequest = fetch('/getVehicles', {cache: 'no-store'});
    const getVehiclesIcActiveRequest = fetch('/shopview/getVehicles?ignoreAppFilters=true', {cache: 'no-store'});
    const getVehiclesUnfilteredRequest = fetch('/getVehiclesUnfiltered', {cache: 'no-store'});
    const getZonesRequest = fetch('/getZonesData?unfiltered=true', {cache: 'no-store'});
    const getAssetLabelsRequest = fetch('/getAssetLabels', {cache: 'no-store'});
    const getBtTagsRequest = fetch('/getBtTags', {cache: 'no-store'});
    const getBtHubsRequest = fetch('/getBtHubs', {cache: 'no-store'});
    const getKeyFobsRequest = fetch('/settings/getKeyFobs', {cache: 'no-store'});
    const getInspectionItemsRequest = fetch('/getInspectionItems', {cache: 'no-store'});
    const getScorecardRequest = fetch('/statsview/getScorecards', {cache: 'no-store'});
    const getImplementLabelsRequest = fetch('/settings/implementLabels', {cache: 'no-store'});

    const [
      getDevicesResponse,
      getDatabaseUsersResponse,
      getOperatorsResponse,
      getVehiclesResponse,
      getVehiclesIcActiveResponse,
      getVehiclesUnfilteredResponse,
      getZonesResponse,
      getAssetLabelsResponse,
      getBtTagsResponse,
      getBtHubsResponse,
      getKeyFobsResponse,
      getInspectionItemsResponse,
      getSettingsGroupsResponse,
      getScorecardResponse,
      getImplementLabelsResponse,
    ] = await Promise.all([
      getDevicesRequest,
      getDatabaseUsersRequest,
      getOperatorsRequest,
      getVehiclesRequest,
      getVehiclesIcActiveRequest,
      getVehiclesUnfilteredRequest,
      getZonesRequest,
      getAssetLabelsRequest,
      getBtTagsRequest,
      getBtHubsRequest,
      getKeyFobsRequest,
      getInspectionItemsRequest,
      getSettingsGroupsRequest,
      getScorecardRequest,
      getImplementLabelsRequest,
    ]);

    const devices = await getDevicesResponse.json();
    const databaseUsers = await getDatabaseUsersResponse.json();
    const operatorsPayload = await getOperatorsResponse.json();
    const operators = Array.isArray(operatorsPayload?.operators) ? operatorsPayload.operators : [];
    const vehicles = await getVehiclesResponse.json();
    const vehicleIcActive = await getVehiclesIcActiveResponse.json();
    const vehiclesUnfiltered = await getVehiclesUnfilteredResponse.json();
    const zones = await getZonesResponse.json();
    const assetLabels = await getAssetLabelsResponse.json();
    const btTags = await getBtTagsResponse.json();
    const btHubs = await getBtHubsResponse.json();
    const keyFobsPayload = await getKeyFobsResponse.json();
    const keyFobs = Array.isArray(keyFobsPayload?.keyFobs)
      ? keyFobsPayload.keyFobs
      : Array.isArray(keyFobsPayload)
      ? keyFobsPayload
      : [];
    const inspectionItems = await getInspectionItemsResponse.json();
    const settingsGroups = await getSettingsGroupsResponse.json();
    const vehicleLabels = getState().app.vehicleLabels || [];
    const implementLabelsData = await getImplementLabelsResponse.json();
    let scorecards = await getScorecardResponse.json();
    scorecards = scorecards.data;
    scorecards.sort((a, b) => {
      if (a.data.startDate > b.data.startDate) {
        return 1;
      } else {
        return -1;
      }
    });

    inspectionItems.forEach((inspItem) => {
      if (!inspItem.hasOwnProperty('service_on_failure')) {
        inspItem['service_on_failure'] = false;
      }
    });
    sortDatabaseUsers(databaseUsers);

    dispatch(settingsSlice.actions.updateScorecards(scorecards));
    dispatch(settingsSlice.actions.updateDevices(devices));
    dispatch(settingsSlice.actions.updateDatabaseUsers(databaseUsers));
    dispatch(settingsSlice.actions.updateOperators(operators));
    dispatch(settingsSlice.actions.updateSettingsGroups(settingsGroups));
    dispatch(settingsSlice.actions.updateVehicleLabels(vehicleLabels));
    dispatch(settingsSlice.actions.updateVehiclesData(vehicles));
    dispatch(settingsSlice.actions.updateVehiclesIcActiveData(vehicleIcActive));
    dispatch(settingsSlice.actions.updateVehiclesUnfilteredData(vehiclesUnfiltered));
    dispatch(settingsSlice.actions.updateZonesData(zones));
    dispatch(settingsSlice.actions.updateLabelsData(assetLabels));
    dispatch(settingsSlice.actions.updateBtTagsData(btTags));
    dispatch(settingsSlice.actions.updateBtHubsData(btHubs));
    dispatch(settingsSlice.actions.updateKeyFobsData(keyFobs));
    dispatch(settingsSlice.actions.updateInspectionItemsData(inspectionItems));
    dispatch(settingsSlice.actions.updateLoading(false));
    dispatch(settingsSlice.actions.updateTableLoading({table: 'assetLabels', status: false}));
    dispatch(settingsSlice.actions.updateTableLoading({table: 'inspectionItems', status: false}));
    dispatch(settingsSlice.actions.updateImplementLabelsDict(implementLabelsData.data));
    dispatch(settingsSlice.actions.setDataLoaded(true));
    dispatch(settingsSlice.actions.updateRefreshing({key: 'users', status: false}));
    dispatch(settingsSlice.actions.updateRefreshing({key: 'assets', status: false}));
    dispatch(settingsSlice.actions.updateRefreshing({key: 'reports', status: false}));
  };
}

function getGlobalVehiclesDatabase() {
  return async (dispatch) => {
    const getVehicleDatabaseRequest = fetch('/settings/getGlobalVehiclesDatabase', {cache: 'no-store'});
    const [vehicleDatabaseResponse] = await Promise.all([getVehicleDatabaseRequest]);
    const vehicleDatabase = await vehicleDatabaseResponse.json();
    dispatch(settingsSlice.actions.updateGobalVehicleDatabase(vehicleDatabase));
  };
}

function getVehicleSettingsData() {
  return async (dispatch, getState) => {
    dispatch(settingsSlice.actions.updateLoading(true));

    const getDevicesRequest = fetch('/getDevices', {cache: 'no-store'});
    const getVehiclesRequest = fetch('/getVehicles', {cache: 'no-store'});
    const getVehiclesIcActiveRequest = fetch('/shopview/getVehicles?ignoreAppFilters=true', {cache: 'no-store'});
    const getVehiclesUnfilteredRequest = fetch('/getVehiclesUnfiltered', {cache: 'no-store'});

    const [getDevicesResponse, getVehiclesResponse, getVehiclesIcActiveResponse, getVehiclesUnfilteredResponse] =
      await Promise.all([
        getDevicesRequest,
        getVehiclesRequest,
        getVehiclesIcActiveRequest,
        getVehiclesUnfilteredRequest,
      ]);

    const devices = await getDevicesResponse.json();
    const vehicleLabels = getState().app.vehicleLabels || [];
    const vehicles = await getVehiclesResponse.json();
    const vehicleIcActive = await getVehiclesIcActiveResponse.json();
    const vehiclesUnfiltered = await getVehiclesUnfilteredResponse.json();

    dispatch(settingsSlice.actions.updateDevices(devices));
    dispatch(settingsSlice.actions.updateVehicleLabels(vehicleLabels));
    dispatch(settingsSlice.actions.updateVehiclesData(vehicles));
    dispatch(settingsSlice.actions.updateVehiclesIcActiveData(vehicleIcActive));
    dispatch(settingsSlice.actions.updateVehiclesUnfilteredData(vehiclesUnfiltered));
  };
}

function getTaskConfigsData() {
  return async (dispatch, getState) => {
    const hasExistingData = Object.keys(getState().settings.taskConfigDict || {}).length > 0;

    if (hasExistingData) {
      dispatch(settingsSlice.actions.updateRefreshing({key: 'tasks', status: true}));
    } else {
      dispatch(settingsSlice.actions.updateTableLoading({table: 'task', status: true}));
    }

    const getTaskConfigsRequest = fetch('/getTaskConfigs', {cache: 'no-store'});
    const getTaskConfigsResponse = await getTaskConfigsRequest;
    const taskConfigIdDict = await getTaskConfigsResponse.json();

    dispatch(settingsSlice.actions.updateTableLoading({table: 'task', status: false}));
    dispatch(settingsSlice.actions.updateTaskConfigDict(taskConfigIdDict));
    dispatch(settingsSlice.actions.setTaskConfigsLoaded(true));
    dispatch(settingsSlice.actions.updateRefreshing({key: 'tasks', status: false}));
  };
}

function getImplementsData() {
  return async (dispatch, getState) => {
    const hasExistingData = Object.keys(getState().settings.implementSNDict || {}).length > 0;

    if (!hasExistingData) {
      dispatch(settingsSlice.actions.updateTableLoading({table: 'implements', status: true}));
    }

    const [getImplementsResponse, getImplementsFilteredResponse] = await Promise.all([
      fetch('/getImplements', {cache: 'no-store'}),
      fetch('/getImplementsFiltered', {cache: 'no-store'}),
    ]);

    const implementSNDict = await getImplementsResponse.json();
    const implementsFiltered = await getImplementsFilteredResponse.json();

    dispatch(settingsSlice.actions.updateTableLoading({table: 'implements', status: false}));
    dispatch(settingsSlice.actions.updateImplementSNDict(implementSNDict));
    dispatch(settingsSlice.actions.updateImplementsFiltered(implementsFiltered));
    dispatch(settingsSlice.actions.setImplementsLoaded(true));
  };
}

function getReportConfigsData() {
  return async (dispatch, getState) => {
    const hasExistingData = Object.keys(getState().settings.reportConfigsDict || {}).length > 0;

    if (hasExistingData) {
      dispatch(settingsSlice.actions.updateRefreshing({key: 'reports', status: true}));
    }

    const getReportConfigsRequest = fetch('/settings/getReportConfigs', {cache: 'no-store'});

    const getReportConfigsResponse = await getReportConfigsRequest;
    const reportConfigsDict = await getReportConfigsResponse.json();

    dispatch(settingsSlice.actions.updateReportConfigsDict(reportConfigsDict));
    dispatch(settingsSlice.actions.updateRefreshing({key: 'reports', status: false}));
  };
}

function getUpdatedVehicles() {
  return async (dispatch) => {
    const getVehiclesRequest = fetch('/getVehicles', {cache: 'no-store'});

    const getVehiclesResponse = await getVehiclesRequest;
    const vehicles = await getVehiclesResponse.json();

    dispatch(settingsSlice.actions.updateVehiclesData(vehicles));
  };
}

// Targeted refresh functions - only fetch specific data needed for each operation
function sortDatabaseUsers(databaseUsers) {
  databaseUsers.sort((a, b) => {
    if (a.archived && !b.archived) return 1;
    if (b.archived && !a.archived) return -1;
    if (a.username.includes('REPORTS-ONLY') && !b.username.includes('REPORTS-ONLY')) return 1;
    if (b.username.includes('REPORTS-ONLY') && !a.username.includes('REPORTS-ONLY')) return -1;
    if (a.email < b.email) return -1;
    if (a.email > b.email) return 1;
    if (a.username < b.username) return -1;
    if (a.username > b.username) return 1;
    return 0;
  });
}

function refreshUsersData() {
  return async (dispatch) => {
    const [usersResponse, groupsResponse, operatorsResponse] = await Promise.all([
      fetch('/settings/getDatabaseUsers', {cache: 'no-store'}),
      fetch('/settings/getSettingsGroups', {cache: 'no-store'}),
      fetch('/settings/getOperators?includeArchived=true', {cache: 'no-store'}),
    ]);

    const databaseUsers = await usersResponse.json();
    const settingsGroups = await groupsResponse.json();
    const operatorsPayload = await operatorsResponse.json();
    const operators = Array.isArray(operatorsPayload?.operators) ? operatorsPayload.operators : [];
    console.log(databaseUsers)
    sortDatabaseUsers(databaseUsers);

    dispatch(settingsSlice.actions.updateDatabaseUsers(databaseUsers));
    dispatch(settingsSlice.actions.updateSettingsGroups(settingsGroups));
    dispatch(settingsSlice.actions.updateOperators(operators));
    dispatch(settingsSlice.actions.updateLoading(false));
    return {databaseUsers, operators, settingsGroups};
  };
}

function refreshVehiclesData() {
  return async (dispatch) => {
    const [devicesRes, vehiclesRes, vehiclesIcActiveRes, vehiclesUnfilteredRes, zonesRes] = await Promise.all([
      fetch('/getDevices', {cache: 'no-store'}),
      fetch('/getVehicles', {cache: 'no-store'}),
      fetch('/shopview/getVehicles?ignoreAppFilters=true', {cache: 'no-store'}),
      fetch('/getVehiclesUnfiltered', {cache: 'no-store'}),
      fetch('/getZonesData?unfiltered=true', {cache: 'no-store'}),
    ]);

    const devices = await devicesRes.json();
    const vehicles = await vehiclesRes.json();
    const vehicleIcActive = await vehiclesIcActiveRes.json();
    const vehiclesUnfiltered = await vehiclesUnfilteredRes.json();
    const zones = await zonesRes.json();

    dispatch(settingsSlice.actions.updateDevices(devices));
    dispatch(settingsSlice.actions.updateVehiclesData(vehicles));
    dispatch(settingsSlice.actions.updateVehiclesIcActiveData(vehicleIcActive));
    dispatch(settingsSlice.actions.updateVehiclesUnfilteredData(vehiclesUnfiltered));
    dispatch(settingsSlice.actions.updateZonesData(zones));
  };
}

function refreshLabelsData() {
  return async (dispatch) => {
    const response = await fetch('/getAssetLabels', {cache: 'no-store'});
    const assetLabels = await response.json();
    dispatch(settingsSlice.actions.updateLabelsData(assetLabels));
    dispatch(settingsSlice.actions.updateTableLoading({table: 'assetLabels', status: false}));
  };
}

function refreshVehicleLabelsData() {
  return async (dispatch) => {
    const response = await fetch('/getVehicleLabels', {cache: 'no-store'});
    const vehicleLabels = await response.json();
    dispatch(settingsSlice.actions.updateVehicleLabels(vehicleLabels));
  };
}

function refreshBtTagsData() {
  return async (dispatch) => {
    const response = await fetch('/getBtTags', {cache: 'no-store'});
    const btTags = await response.json();
    dispatch(settingsSlice.actions.updateBtTagsData(btTags));
  };
}

function refreshKeyFobsData() {
  return async (dispatch) => {
    const response = await fetch('/settings/getKeyFobs', {cache: 'no-store'});
    const payload = await response.json();
    const keyFobs = Array.isArray(payload?.keyFobs) ? payload.keyFobs : Array.isArray(payload) ? payload : [];
    dispatch(settingsSlice.actions.updateKeyFobsData(keyFobs));
    return keyFobs;
  };
}

function refreshBtHubsData() {
  return async (dispatch) => {
    const response = await fetch('/getBtHubs', {cache: 'no-store'});
    const btHubs = await response.json();
    dispatch(settingsSlice.actions.updateBtHubsData(btHubs));
  };
}

function refreshInspectionItemsData() {
  return async (dispatch) => {
    const response = await fetch('/getInspectionItems', {cache: 'no-store'});
    const inspectionItems = await response.json();

    inspectionItems.forEach((inspItem) => {
      if (!Object.prototype.hasOwnProperty.call(inspItem, 'service_on_failure')) {
        inspItem['service_on_failure'] = false;
      }
    });

    dispatch(settingsSlice.actions.updateInspectionItemsData(inspectionItems));
    dispatch(settingsSlice.actions.updateTableLoading({table: 'inspectionItems', status: false}));
  };
}

function refreshScorecardsData() {
  return async (dispatch) => {
    const response = await fetch('/statsview/getScorecards', {cache: 'no-store'});
    let scorecards = await response.json();
    scorecards = scorecards.data;
    scorecards.sort((a, b) => {
      return a.data.startDate > b.data.startDate ? 1 : -1;
    });
    dispatch(settingsSlice.actions.updateScorecards(scorecards));
  };
}

function refreshImplementsData() {
  return async (dispatch) => {
    const [response, filteredResponse] = await Promise.all([
      fetch('/getImplements', {cache: 'no-store'}),
      fetch('/getImplementsFiltered', {cache: 'no-store'}),
    ]);
    const implementSNDict = await response.json();
    const implementsFiltered = await filteredResponse.json();
    dispatch(settingsSlice.actions.updateImplementSNDict(implementSNDict));
    dispatch(settingsSlice.actions.updateImplementsFiltered(implementsFiltered));
    dispatch(settingsSlice.actions.updateTableLoading({table: 'implements', status: false}));
  };
}

function refreshTaskConfigsData() {
  return async (dispatch) => {
    const response = await fetch('/getTaskConfigs', {cache: 'no-store'});
    const taskConfigIdDict = await response.json();
    dispatch(settingsSlice.actions.updateTaskConfigDict(taskConfigIdDict));
    dispatch(settingsSlice.actions.updateTableLoading({table: 'task', status: false}));
  };
}

function refreshReportConfigsData() {
  return async (dispatch) => {
    const response = await fetch('/settings/getReportConfigs', {cache: 'no-store'});
    const reportConfigsDict = await response.json();
    dispatch(settingsSlice.actions.updateReportConfigsDict(reportConfigsDict));
  };
}

export {
  getSettingsData,
  getTaskConfigsData,
  getImplementsData,
  getReportConfigsData,
  getUpdatedVehicles,
  getVehicleSettingsData,
  getGlobalVehiclesDatabase,
  refreshUsersData,
  refreshVehiclesData,
  refreshLabelsData,
  refreshVehicleLabelsData,
  refreshBtTagsData,
  refreshKeyFobsData,
  refreshBtHubsData,
  refreshInspectionItemsData,
  refreshScorecardsData,
  refreshImplementsData,
  refreshTaskConfigsData,
  refreshReportConfigsData,
};
export const {
  triggerGetSettingsData,
  triggerGetVehicleSettingsData,
  updateLoading,
  updateTableLoading,
  updateSettingsGroups,
  updateOperators,
  updateVehicleLabels,
  updateDevices,
  setImplementsBulkEditMode,
  updateBulkEditVehicles,
  updateBulkEditImplements,
  initializeBulkEditImplements,
  initializeBulkEditVehicles,
  setImplementsBulkEditErrors,
  updateBulkEditTasks,
  initializeBulkEditTasks,
  setTasksBulkEditErrors,
  setTasksBulkEditMode,
  setAssetLabelsBulkEditErrors,
  setAssetLabelsBulkEditMode,
  initializeBulkEditAssetLabels,
  updateBulkEditAssetLabels,
  setVehiclesBulkEditErrors,
  initializeBulkEditInspectionItems,
  setInspectionItemsBulkEditMode,
  setInspectionItemsBulkEditErrors,
  updateBulkEditInspectionItems,
  updateLabelsData,
  updateInspectionItemsData,
  updateGobalVehicleDatabase,
  updateImplementLabelsDict,
  updateImplementLastLocation,
  updateUsersTabVisibleColumns,
} = settingsSlice.actions;
export default settingsSlice.reducer;
